// utils/exportUtils.js
import * as XLSX from "xlsx";

/**
 * Export utilization data to Excel with dynamic sections
 * @param {string} fileName - Base filename
 * @param {Array} sections - Array of section objects with title, columns, and data
 * @param {Object} filters - Filter values to include in filename
 */
export const exportExcel = (fileName, sections, filters = {}) => {
  const workbook = XLSX.utils.book_new();
  
  // Helper function to add filter information to worksheet
  const addFilterInformation = (worksheet, filters) => {
    if (!filters || Object.keys(filters).length === 0) return 0;
    
    let currentRow = 1;
    
    // Title
    worksheet[`A${currentRow}`] = { v: 'Applied Filters:', t: 's', s: { font: { bold: true, size: 12 } } };
    currentRow += 2;
    
    // Date range
    if (filters.dateRange && filters.dateRange.from && filters.dateRange.to) {
      const fromDate = new Date(filters.dateRange.from).toLocaleDateString();
      const toDate = new Date(filters.dateRange.to).toLocaleDateString();
      worksheet[`A${currentRow}`] = { v: 'Date Range:', t: 's', s: { font: { bold: true } } };
      worksheet[`B${currentRow}`] = { v: `${fromDate} - ${toDate}`, t: 's' };
      currentRow++;
    }
    
    // Company
    if (filters.company && filters.company !== 'all' && filters.companyName) {
      worksheet[`A${currentRow}`] = { v: 'Company:', t: 's', s: { font: { bold: true } } };
      worksheet[`B${currentRow}`] = { v: filters.companyName, t: 's' };
      currentRow++;
    }
    
    // Department 
    if (filters.department && filters.department !== 'all' && filters.departmentName) {
      worksheet[`A${currentRow}`] = { v: 'Department:', t: 's', s: { font: { bold: true } } };
      worksheet[`B${currentRow}`] = { v: filters.departmentName, t: 's' };
      currentRow++;
    }
    
    // Employee
    if (filters.employee && filters.employee !== 'all' && filters.employeeName) {
      worksheet[`A${currentRow}`] = { v: 'Employee:', t: 's', s: { font: { bold: true } } };
      worksheet[`B${currentRow}`] = { v: filters.employeeName, t: 's' };
      currentRow++;
    }
    
    // Client
    if (filters.client && filters.client !== 'all' && filters.clientName) {
      worksheet[`A${currentRow}`] = { v: 'Client:', t: 's', s: { font: { bold: true } } };
      worksheet[`B${currentRow}`] = { v: filters.clientName, t: 's' };
      currentRow++;
    }
    
    // Job Number
    if (filters.jobNumber && filters.jobNumber !== 'all' && filters.jobName) {
      worksheet[`A${currentRow}`] = { v: 'Job:', t: 's', s: { font: { bold: true } } };
      worksheet[`B${currentRow}`] = { v: filters.jobName, t: 's' };
      currentRow++;
    }
    
    // Add empty row separator
    currentRow += 2;
    
    return currentRow - 1; // Return last used row
  };
  
  // Helper function to format cell data based on column key and type
  const formatExcelData = (item, key, type) => {
    let value = item[key];
    
    if (value === null || value === undefined) {
      return '';
    }
    
    switch (type) {
      case 'percentage':
        return parseFloat(value) / 100; // Excel will format as percentage
      case 'number':
        return parseFloat(value) || 0;
      case 'currency':
        return parseFloat(value) || 0; // Excel will format as currency
      case 'date':
        return new Date(value);
      default:
        return value.toString();
    }
  };
  
  // Create a worksheet for each section or combine all sections
  if (sections.length === 1) {
    // Single section - use section name as sheet name
    const section = sections[0];
    
    // Create empty worksheet
    const worksheet = {};
    
    // Add filter information at the top
    const filterEndRow = addFilterInformation(worksheet, filters);
    
    // Create main data rows
    const excelData = section.data.map(item => {
      const row = {};
      section.columnKeys.forEach((key, index) => {
        const header = section.columns[index];
        row[header] = formatExcelData(item, key, section.columnTypes[index]);
      });
      return row;
    });
    
    // Add data starting after filters
    const dataStartRow = filterEndRow + 1;
    const dataWorksheet = XLSX.utils.json_to_sheet(excelData);
    
    // Copy data to main worksheet with offset
    const dataRange = XLSX.utils.decode_range(dataWorksheet['!ref']);
    for (let row = dataRange.s.r; row <= dataRange.e.r; row++) {
      for (let col = dataRange.s.c; col <= dataRange.e.c; col++) {
        const sourceAddr = XLSX.utils.encode_cell({ r: row, c: col });
        const targetAddr = XLSX.utils.encode_cell({ r: row + dataStartRow, c: col });
        if (dataWorksheet[sourceAddr]) {
          worksheet[targetAddr] = dataWorksheet[sourceAddr];
        }
      }
    }
    
    // Set worksheet range
    const totalRows = dataRange.e.r + dataStartRow;
    const totalCols = Math.max(dataRange.e.c, 1); // At least 2 columns for filters
    worksheet['!ref'] = XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: totalRows, c: totalCols }
    });
    
    // Apply column formatting to data section only
    for (let col = 0; col <= dataRange.e.c; col++) {
      const columnType = section.columnTypes[col];
      
      // Set column widths
      if (!worksheet['!cols']) worksheet['!cols'] = [];
      worksheet['!cols'][col] = { width: columnType === 'percentage' ? 12 : 15 };
      
      // Format cells based on type (data rows only)
      for (let row = dataStartRow + 1; row <= totalRows; row++) {
        const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
        if (worksheet[cellAddress] && columnType) {
          switch (columnType) {
            case 'percentage':
              worksheet[cellAddress].z = '0.0%';
              break;
            case 'currency':
              worksheet[cellAddress].z = '$#,##0.00';
              break;
            case 'number':
              worksheet[cellAddress].z = '#,##0';
              break;
            default:
              break;
          }
        }
      }
    }
    
    // Sanitize sheet name by removing invalid characters and limiting length
    const sanitizedName = section.title
      .replace(/[:\\/?*[\]]/g, '') // Remove invalid characters
      .replace(/\s+/g, ' ') // Replace multiple spaces with single space
      .trim()
      .substring(0, 31); // Excel sheet name limit
    
    XLSX.utils.book_append_sheet(workbook, worksheet, sanitizedName);
    
  } else {
    // Multiple sections - create separate sheets for each
    sections.forEach((section, index) => {
      // Create empty worksheet
      const worksheet = {};
      
      // Add filter information at the top (only on first sheet)
      const filterEndRow = index === 0 ? addFilterInformation(worksheet, filters) : -1;
      
      const excelData = section.data.map(item => {
        const row = {};
        section.columnKeys.forEach((key, colIndex) => {
          const header = section.columns[colIndex];
          row[header] = formatExcelData(item, key, section.columnTypes[colIndex]);
        });
        return row;
      });
      
      // Add data starting after filters (or from top if no filters)
      const dataStartRow = filterEndRow >= 0 ? filterEndRow + 1 : 0;
      const dataWorksheet = XLSX.utils.json_to_sheet(excelData);
      
      // Copy data to main worksheet with offset
      const dataRange = XLSX.utils.decode_range(dataWorksheet['!ref']);
      for (let row = dataRange.s.r; row <= dataRange.e.r; row++) {
        for (let col = dataRange.s.c; col <= dataRange.e.c; col++) {
          const sourceAddr = XLSX.utils.encode_cell({ r: row, c: col });
          const targetAddr = XLSX.utils.encode_cell({ r: row + dataStartRow, c: col });
          if (dataWorksheet[sourceAddr]) {
            worksheet[targetAddr] = dataWorksheet[sourceAddr];
          }
        }
      }
      
      // Set worksheet range
      const totalRows = dataRange.e.r + dataStartRow;
      const totalCols = Math.max(dataRange.e.c, filterEndRow >= 0 ? 1 : 0);
      worksheet['!ref'] = XLSX.utils.encode_range({
        s: { r: 0, c: 0 },
        e: { r: totalRows, c: totalCols }
      });
      
      // Apply column formatting to data section only
      for (let col = 0; col <= dataRange.e.c; col++) {
        const columnType = section.columnTypes[col];
        
        // Set column widths
        if (!worksheet['!cols']) worksheet['!cols'] = [];
        worksheet['!cols'][col] = { width: columnType === 'percentage' ? 12 : 15 };
        
        // Format cells based on type (data rows only)
        for (let row = dataStartRow + 1; row <= totalRows; row++) {
          const cellAddress = XLSX.utils.encode_cell({ r: row, c: col });
          if (worksheet[cellAddress] && columnType) {
            switch (columnType) {
              case 'percentage':
                worksheet[cellAddress].z = '0.0%';
                break;
              case 'currency':
                worksheet[cellAddress].z = '$#,##0.00';
                break;
              case 'number':
                worksheet[cellAddress].z = '#,##0';
                break;
              default:
                break;
            }
          }
        }
      }
      
      // Sanitize sheet name by removing invalid characters and limiting length
      const sanitizedName = section.title
        .replace(/[:\\/?*[\]]/g, '') // Remove invalid characters
        .replace(/\s+/g, ' ') // Replace multiple spaces with single space
        .trim()
        .substring(0, 31); // Excel sheet name limit
      
      XLSX.utils.book_append_sheet(workbook, worksheet, sanitizedName);
    });
  }
  
  // Create filename with date for uniqueness
  const dateStr = new Date().toISOString().split('T')[0];
  const cleanFileName = `${fileName.replace(/\s+/g, "_").toLowerCase()}_${dateStr}`;
  
  XLSX.writeFile(workbook, `${cleanFileName}.xlsx`);
};
